import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  IconButton,
  Paper,
  Avatar,
  Chip,
  Grid,
  useTheme,
  alpha,
  Fade,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Close,
  ArrowForward,
  ArrowBack,
  CheckCircle,
  Business,
  Schedule,
  CalendarMonth,
  Celebration,
  RocketLaunch,
  Web,
  Dashboard,
  Phone,
  ContentCopy,
  OpenInNew,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useCustomTheme } from '../../context/ThemeContext';
import { toast } from 'react-toastify';
import BookingApiService from '../../services/BookingApiService';
import apiService from '../../services/ApiService';
import AIAssistantPrompts from './AIAssistantPrompts';
import AISuggestionDialog from './AISuggestionDialog';
import AIOnboardingService from '../../services/AIOnboardingService';
import QuickLaunchFlow from './QuickLaunchFlow';
import {
  validatePhoneNumber,
  validateEmail,
  validateBusinessName,
  sanitizeText
} from '../../utils/validation';
import { useCentralProfilePrefill, mapCentralToBooker } from '../../hooks/useCentralProfilePrefill';

const STEPS = [
  'Welcome',
  'Business Profile',
  'First Service',
  'Availability',
  'Success!'
];

const INDUSTRY_OPTIONS = [
  { value: 'medical', label: 'Healthcare & Medical', theme: 'medical' },
  { value: 'beauty', label: 'Beauty & Spa', theme: 'beauty' },
  { value: 'fitness', label: 'Fitness & Wellness', theme: 'fitness' },
  { value: 'professional', label: 'Professional Services', theme: 'professional' },
  { value: 'restaurant', label: 'Food & Beverage', theme: 'restaurant' },
  { value: 'automotive', label: 'Automotive', theme: 'automotive' },
  { value: 'creative', label: 'Creative & Design', theme: 'creative' },
  { value: 'other', label: 'Other', theme: 'dark-maximalist' }
];

const BRAND_VIBE_OPTIONS = [
  { value: 'modern', label: 'Modern & Clean' },
  { value: 'luxury', label: 'Luxury & Premium' },
  { value: 'friendly', label: 'Warm & Friendly' },
  { value: 'bold', label: 'Bold & Energetic' },
  { value: 'classic', label: 'Classic & Timeless' },
  { value: 'minimal', label: 'Minimal & Sleek' }
];

const SERVICE_TEMPLATES = {
  medical: [
    { name: 'Consultation', duration: 30, price: 150 },
    { name: 'Follow-up Visit', duration: 15, price: 75 },
    { name: 'Initial Assessment', duration: 60, price: 200 }
  ],
  beauty: [
    { name: 'Haircut', duration: 45, price: 50 },
    { name: 'Hair Color', duration: 120, price: 120 },
    { name: 'Manicure', duration: 30, price: 35 }
  ],
  fitness: [
    { name: 'Personal Training', duration: 60, price: 80 },
    { name: 'Group Class', duration: 45, price: 25 },
    { name: 'Nutrition Consultation', duration: 30, price: 60 }
  ],
  professional: [
    { name: 'Consultation', duration: 60, price: 150 },
    { name: 'Strategy Session', duration: 90, price: 250 },
    { name: 'Workshop', duration: 120, price: 300 }
  ],
  restaurant: [
    { name: 'Table for 2', duration: 90, price: 0 },
    { name: 'Table for 4', duration: 120, price: 0 },
    { name: 'Private Dining', duration: 180, price: 500 }
  ],
  automotive: [
    { name: 'Oil Change', duration: 30, price: 50 },
    { name: 'Tire Rotation', duration: 45, price: 40 },
    { name: 'Inspection', duration: 60, price: 75 }
  ],
  creative: [
    { name: 'Design Consultation', duration: 60, price: 120 },
    { name: 'Project Review', duration: 45, price: 90 },
    { name: 'Workshop', duration: 120, price: 200 }
  ],
  other: [
    { name: 'Service', duration: 60, price: 100 },
    { name: 'Consultation', duration: 30, price: 50 },
    { name: 'Session', duration: 90, price: 150 }
  ]
};

const BUSINESS_TYPES = [
  {
    value: 'solo',
    label: "Solo Professional",
    description: 'Just me managing my schedule',
    features: {
      interfaceMode: 'simple',
      hideFeatures: ['leads', 'automation', 'crm'],
      showTip: 'Perfect! We\'ll keep things simple - bookings, clients, and website building.'
    }
  },
  {
    value: 'small',
    label: 'Small Team',
    description: '2-10 staff members',
    features: {
      interfaceMode: 'balanced',
      hideFeatures: [],
      showTip: 'Great! You\'ll have access to CRM and basic automation tools.'
    }
  },
  {
    value: 'medium',
    label: 'Growing Business',
    description: '10-50 staff, multiple services',
    features: {
      interfaceMode: 'balanced',
      hideFeatures: [],
      showTip: 'Excellent! Full CRM, automation, and advanced team management.'
    }
  },
  {
    value: 'business',
    label: 'Business',
    description: 'Multiple locations, advanced needs',
    features: {
      interfaceMode: 'advanced',
      hideFeatures: [],
      showTip: 'Perfect! You\'ll have access to all business features and integrations.'
    }
  }
];

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Toronto', label: 'Toronto (ET)' },
  { value: 'America/Vancouver', label: 'Vancouver (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT)' }
];

const DEFAULT_AVAILABILITY = {
  monday: { enabled: true, start: '09:00', end: '17:00' },
  tuesday: { enabled: true, start: '09:00', end: '17:00' },
  wednesday: { enabled: true, start: '09:00', end: '17:00' },
  thursday: { enabled: true, start: '09:00', end: '17:00' },
  friday: { enabled: true, start: '09:00', end: '17:00' },
  saturday: { enabled: false, start: '09:00', end: '17:00' },
  sunday: { enabled: false, start: '09:00', end: '17:00' }
};

const OnboardingWizard = ({ open, onClose, onComplete }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setTheme } = useCustomTheme();
  const { currentUser, userProfile, updateUserProfile } = useAuth();

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    businessType: 'solo',
    businessName: '',
    businessSlug: '',
    businessDescription: '',
    ownerName: '',
    foundedYear: new Date().getFullYear(),
    tagline: '',
    brandVibe: 'modern',
    serviceArea: '',
    signatureServices: ['', '', ''],
    businessPhone: '',
    businessEmail: '',
    businessAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    },
    industry: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
    serviceName: '',
    serviceDuration: 60,
    servicePrice: 100,
    availability: DEFAULT_AVAILABILITY
  });

  // Validation errors
  const [validationErrors, setValidationErrors] = useState({
    businessName: '',
    businessDescription: '',
    businessPhone: '',
    businessEmail: ''
  });

  // AI Assistant state
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [aiType, setAiType] = useState(null);
  const [aiDialogTitle, setAiDialogTitle] = useState('');

  // Quick Launch state
  const [quickLaunchOpen, setQuickLaunchOpen] = useState(false);

  // Central Business Profile (read-only pre-fill) — only fetch when wizard is open
  const { profile: centralProfile, loading: centralLoading } = useCentralProfilePrefill({ autoFetch: open });
  const [centralPrefillApplied, setCentralPrefillApplied] = useState(false);

  // Pre-fill from Central Business Profile (if user has one from another FutureSense app)
  useEffect(() => {
    if (centralLoading || centralPrefillApplied) return;
    if (!centralProfile) return;

    const mapped = mapCentralToBooker(centralProfile);
    if (!mapped) return;

    // Only pre-fill if we have meaningful data and form is empty
    if (mapped.businessName && !data.businessName) {
      console.log('✅ [Onboarding] Pre-filling from Central Business Profile');
      setData(prev => ({
        ...prev,
        businessName: mapped.businessName || prev.businessName,
        businessPhone: mapped.businessPhone || prev.businessPhone,
        businessEmail: mapped.businessEmail || prev.businessEmail,
        tagline: mapped.tagline || prev.tagline,
        businessDescription: mapped.businessDescription || prev.businessDescription,
        industry: mapped.industry || prev.industry,
        timezone: mapped.timezone || prev.timezone,
        businessAddress: mapped.businessAddress || prev.businessAddress
      }));
      toast.info('We found your business profile and pre-filled some fields!');
      setCentralPrefillApplied(true);
    }
  }, [centralProfile, centralLoading, centralPrefillApplied, data.businessName]);

  // Auto-populate business name from user profile or Smart Start Data
  useEffect(() => {
    // Skip if we already pre-filled from central profile
    if (centralPrefillApplied) return;

    // Check for "Smart Start" booking data
    const bookingStartDataRaw = sessionStorage.getItem('bookingStartData');
    const websiteStartDataRaw = sessionStorage.getItem('websiteStartData');
    
    if (bookingStartDataRaw) {
      try {
        const startData = JSON.parse(bookingStartDataRaw);
        console.log('Hydrating from Booking Smart Start:', startData);

        setData(prev => ({
          ...prev,
          businessName: startData.businessName || prev.businessName,
          industry: startData.industry || prev.industry,
          serviceName: startData.serviceName || prev.serviceName,
          serviceDuration: parseInt(startData.duration) || prev.serviceDuration,
          servicePrice: parseFloat(startData.price) || prev.servicePrice
        }));

        // Clear it so it doesn't persist forever
        sessionStorage.removeItem('bookingStartData');

        if (startData.businessName && startData.industry) {
             toast.success(`Welcome back, ${startData.businessName}! We've saved your progress.`);
        }
      } catch (e) {
        console.error('Failed to parse booking start data', e);
      }
    } else if (websiteStartDataRaw) {
      try {
        const startData = JSON.parse(websiteStartDataRaw);
        console.log('Hydrating from Website Smart Start:', startData);

        setData(prev => ({
          ...prev,
          businessName: startData.businessName || prev.businessName,
          industry: startData.industry || prev.industry
        }));

        // We DON'T clear websiteStartData here because WebsiteBuilderPage might need it too.
        // It's safer to let the user finish onboarding then go to website builder.
        
        if (startData.businessName && startData.industry) {
             toast.success(`Welcome back, ${startData.businessName}! Let's finish your profile.`);
        }
      } catch (e) {
        console.error('Failed to parse website start data', e);
      }
    } else if (userProfile?.displayName && !data.businessName) {
      setData(prev => ({
        ...prev,
        businessName: userProfile.displayName
      }));
    }
  }, [userProfile, centralPrefillApplied]); // Run once on mount (or when profile loads)

  // Update service template when industry changes
  useEffect(() => {
    if (data.industry && activeStep === 2) {
      const templates = SERVICE_TEMPLATES[data.industry] || SERVICE_TEMPLATES.other;
      const template = templates[0];
      setData(prev => ({
        ...prev,
        serviceName: template.name,
        serviceDuration: template.duration,
        servicePrice: template.price
      }));
    }
  }, [data.industry, activeStep]);

  // Validation helper functions
  const validateBusinessNameField = (value) => {
    const validation = validateBusinessName(value);
    if (!validation.isValid) {
      setValidationErrors(prev => ({ ...prev, businessName: validation.error }));
      return false;
    }
    setValidationErrors(prev => ({ ...prev, businessName: '' }));
    return true;
  };

  const validatePhoneField = (value) => {
    if (!value) return true; // Optional field
    const validation = validatePhoneNumber(value);
    if (!validation.isValid) {
      setValidationErrors(prev => ({ ...prev, businessPhone: validation.error }));
      return false;
    }
    setValidationErrors(prev => ({ ...prev, businessPhone: '' }));
    return true;
  };

  const validateEmailField = (value) => {
    if (!value) return true; // Optional field
    const validation = validateEmail(value);
    if (!validation.isValid) {
      setValidationErrors(prev => ({ ...prev, businessEmail: validation.error }));
      return false;
    }
    setValidationErrors(prev => ({ ...prev, businessEmail: '' }));
    return true;
  };

  // Reader for sharedOnboarding flag — set by Booker or AI-Caller (Connect) wizards
  // when business profile is captured. When true, skip the Business Profile step
  // (data is pre-filled via useCentralProfilePrefill).
  const businessProfileShared = !!userProfile?.sharedOnboarding?.businessProfileCompleted;

  const handleNext = async () => {
    // Skip Business Profile step entirely if it was already completed elsewhere
    if (activeStep === 0 && businessProfileShared) {
      setActiveStep(2);
      return;
    }

    // Validation for each step
    if (activeStep === 1) {
      if (!data.businessName.trim()) {
        toast.error('Please enter your business name');
        return;
      }

      // Validate business name
      if (!validateBusinessNameField(data.businessName)) {
        toast.error('Please fix business name errors');
        return;
      }

      // Validate owner name (required)
      if (!data.ownerName.trim()) {
        toast.error('Please enter the owner/founder name');
        return;
      }

      if (data.ownerName.trim().length < 2) {
        toast.error('Owner name must be at least 2 characters');
        return;
      }

      // Validate phone if provided
      if (data.businessPhone && !validatePhoneField(data.businessPhone)) {
        toast.error('Please enter a valid phone number');
        return;
      }

      // Validate email if provided
      if (data.businessEmail && !validateEmailField(data.businessEmail)) {
        toast.error('Please enter a valid email address');
        return;
      }

      if (!data.industry) {
        toast.error('Please select your industry');
        return;
      }
    }

    if (activeStep === 2) {
      if (!data.serviceName.trim()) {
        toast.error('Please enter a service name');
        return;
      }
      if (data.serviceDuration < 5) {
        toast.error('Service duration must be at least 5 minutes');
        return;
      }
    }

    // Complete onboarding on last step (before advancing, so failure stays on current step)
    if (activeStep === STEPS.length - 2) {
      await handleComplete();
      return; // handleComplete sets the step on success
    }

    // Move to next step
    if (activeStep < STEPS.length - 1) {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    // Mirror skip on back: jump from First Service back to Welcome when Business Profile is shared
    if (activeStep === 2 && businessProfileShared) {
      setActiveStep(0);
      return;
    }
    setActiveStep(prev => prev - 1);
  };

  // Handle AI prompt click
  const handleAIPromptClick = async (prompt) => {
    setAiError(null);
    setAiSuggestions(null);
    setAiType(prompt.action);
    setAiDialogOpen(true);
    setAiLoading(true);

    // Set dialog title based on action
    const titles = {
      businessName: 'AI Business Name Suggestions',
      services: 'AI Service Suggestions',
      pricing: 'AI Pricing Suggestion',
      availability: 'AI Availability Suggestion',
      description: 'AI Description Generator',
      help: 'AI Assistant'
    };
    setAiDialogTitle(titles[prompt.action] || 'AI Suggestion');

    try {
      let result;

      switch (prompt.action) {
        case 'businessName':
          result = await AIOnboardingService.suggestBusinessNames(
            prompt.params.industry,
            prompt.params.style
          );
          break;

        case 'services':
          result = await AIOnboardingService.suggestServices(
            prompt.params.industry,
            prompt.params.businessType || prompt.params.businessSize
          );
          break;

        case 'pricing':
          result = await AIOnboardingService.suggestPricing(
            prompt.params.serviceName,
            prompt.params.industry
          );
          break;

        case 'availability':
          result = await AIOnboardingService.suggestAvailability(
            prompt.params.industry,
            prompt.params.businessType || prompt.params.businessSize
          );
          break;

        case 'description':
          result = await AIOnboardingService.generateServiceDescription(
            prompt.params.serviceName,
            prompt.params.industry,
            prompt.params.duration,
            prompt.params.price
          );
          break;

        case 'help':
          result = await AIOnboardingService.getQuickHelp(
            prompt.question,
            STEPS[activeStep]
          );
          break;

        default:
          throw new Error('Unknown action type');
      }

      setAiSuggestions(result);
    } catch (error) {
      console.error('AI suggestion error:', error);
      setAiError(error.message || 'Failed to get AI suggestion. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  // Handle accepting AI suggestion
  const handleAcceptSuggestion = (suggestion) => {
    switch (aiType) {
      case 'businessName':
        setData({ ...data, businessName: suggestion.name });
        toast.success(`Business name set to: ${suggestion.name}`);
        break;

      case 'services':
        setData({
          ...data,
          serviceName: suggestion.name,
          serviceDuration: suggestion.duration,
          servicePrice: suggestion.price
        });
        toast.success(`Service "${suggestion.name}" loaded`);
        break;

      case 'pricing':
        setData({ ...data, servicePrice: suggestion.recommended });
        toast.success(`Price set to: $${suggestion.recommended}`);
        break;

      case 'availability':
        const { rationale, ...availabilityData } = suggestion;
        setData({ ...data, availability: availabilityData });
        toast.success('Availability updated');
        break;

      case 'description':
        toast.info('Description copied! You can use it in your service settings later.');
        break;

      case 'help':
        // Help is just informational, no data update
        break;

      default:
        break;
    }

    setAiDialogOpen(false);
  };

  // Handle regenerate suggestion
  const handleRegenerateSuggestion = async () => {
    // Find the current prompt and re-trigger
    setAiLoading(true);
    setAiError(null);

    // Simulate regeneration by adding small variation
    try {
      const style = ['professional', 'modern', 'creative'][Math.floor(Math.random() * 3)];

      let result;
      switch (aiType) {
        case 'businessName':
          result = await AIOnboardingService.suggestBusinessNames(
            data.industry || 'professional',
            style
          );
          break;

        case 'services':
          result = await AIOnboardingService.suggestServices(
            data.industry || 'professional',
            data.businessType || 'solo'
          );
          break;

        default:
          throw new Error('Regeneration not supported for this type');
      }

      setAiSuggestions(result);
    } catch (error) {
      setAiError(error.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      // Get selected business type config
      const businessTypeConfig = BUSINESS_TYPES.find(bt => bt.value === data.businessType);

      // 1. Write onboarding flag FIRST — most critical write.
      //    Even if subsequent steps fail, the wizard won't re-appear.
      //    Also set sharedOnboarding.businessProfileCompleted so Connect/other apps
      //    can skip the redundant Business Profile step on first launch.
      await updateUserProfile({
        bookerOnboardingCompleted: true,
        bookerOnboardingCompletedAt: new Date().toISOString(),
        sharedOnboarding: {
          businessProfileCompleted: true,
          completedAt: new Date().toISOString(),
          completedBy: 'booker'
        }
      });

      // 2. Update CENTRAL user profile with shared business info
      await updateUserProfile({
        businessName: sanitizeText(data.businessName),
        businessSlug: data.businessSlug,
        businessDescription: sanitizeText(data.businessDescription),
        ownerName: sanitizeText(data.ownerName),
        foundedYear: data.foundedYear,
        tagline: sanitizeText(data.tagline),
        brandVibe: data.brandVibe,
        serviceArea: sanitizeText(data.serviceArea),
        signatureServices: data.signatureServices.map(service => sanitizeText(service)).filter(Boolean),
        businessPhone: data.businessPhone,
        businessEmail: sanitizeText(data.businessEmail),
        businessAddress: data.businessAddress,
        industry: data.industry,
        timezone: data.timezone,
        businessType: data.businessType,
        interfaceMode: (() => {
          // Honor pending mode stashed by /websites landing page
          try {
            const pending = localStorage.getItem('nexusPendingInterfaceMode');
            if (pending) {
              localStorage.removeItem('nexusPendingInterfaceMode');
              return pending;
            }
          } catch (e) { /* ignore */ }
          return businessTypeConfig?.features.interfaceMode || 'balanced';
        })(),
        hiddenFeatures: businessTypeConfig?.features.hideFeatures || [],
      });

      // 2. Apply industry theme
      const industryTheme = INDUSTRY_OPTIONS.find(opt => opt.value === data.industry)?.theme || 'dark-maximalist';
      await setTheme(industryTheme);

      // 3. Create first service (only if user doesn't have any services yet)
      try {
        const existingServices = await BookingApiService.getServices();
        if (!existingServices || existingServices.length === 0) {
          await BookingApiService.createService({
            name: data.serviceName,
            duration: data.serviceDuration,
            price: data.servicePrice,
            description: `${data.serviceName} service`,
            isActive: true,
            category: data.industry
          });
          console.log('✅ First service created during onboarding');
        } else {
          console.log('ℹ️ User already has services, skipping service creation');
        }
      } catch (serviceError) {
        // If service creation fails due to quota, that's okay - user can add later
        console.warn('Service creation skipped (likely already exists):', serviceError.message);
      }

      // 4. Save availability settings
      await BookingApiService.updateAvailability(data.availability);

      // 5. Update profile with availability flag (for setup progress tracking)
      await updateUserProfile({
        availabilityConfigured: true,
        availabilityUpdatedAt: new Date().toISOString()
      });

      // 6. Try to create full branding document (Pro/Enterprise plans only)
      // If it fails due to feature gate, minimal branding is already in profile
      try {
        await apiService.localAPI.put('/user/branding', {
          businessName: sanitizeText(data.businessName),
          businessSlug: data.businessSlug || sanitizeText(data.businessName).toLowerCase().replace(/[^a-z0-9-]/g, '-'),
          tagline: sanitizeText(data.tagline),
          contactInfo: {
            phone: data.businessPhone,
            email: sanitizeText(data.businessEmail),
            address: data.businessAddress
          },
          colors: {
            primary: '#06b6d4',
            secondary: '#8b5cf6',
            background: '#020617',
            text: '#f8fafc',
            accent: '#10b981'
          },
          fonts: {
            heading: 'Outfit, Inter, sans-serif',
            body: 'Inter, Roboto, sans-serif'
          }
        }, {
          suppressSubscriptionToast: true  // Suppress 403 toast during onboarding
        });
        console.log('✅ Full branding document created (Pro/Enterprise plan)');
      } catch (brandingError) {
        // Expected for free plans - minimal branding already saved to profile above
        console.log('ℹ️ Using profile-based branding (Free plan or API error):', brandingError.message);
      }

      // Report all setup steps to Central dashboard (fire-and-forget)
      ['business-profile', 'add-service', 'set-availability', 'business-hours'].forEach(
        step => apiService.reportSetupStep(step)
      );

      toast.success('🎉 Onboarding complete! Your booking system is ready!', {
        autoClose: 5000
      });

      // Move to success step (user will choose next action: website builder or dashboard)
      setActiveStep(STEPS.length - 1);

    } catch (error) {
      console.error('Error completing onboarding:', error);
      toast.error('Failed to complete setup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    const stepComponents = {
      0: <WelcomeStep data={data} setData={setData} theme={theme} onQuickLaunch={() => setQuickLaunchOpen(true)} />,
      1: <BusinessProfileStep
           data={data}
           setData={setData}
           theme={theme}
           validationErrors={validationErrors}
           setValidationErrors={setValidationErrors}
         />,
      2: <ServiceStep data={data} setData={setData} theme={theme} />,
      3: <AvailabilityStep data={data} setData={setData} theme={theme} />,
      4: <SuccessStep data={data} theme={theme} onComplete={onComplete} />
    };

    return (
      <Box>
        {stepComponents[activeStep]}
        {activeStep < 4 && (
          <AIAssistantPrompts
            step={activeStep}
            onPromptClick={handleAIPromptClick}
            data={data}
            disabled={loading || aiLoading}
          />
        )}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        // Block backdrop dismiss until user reaches Availability step (activation surface)
        if (activeStep < 3 && reason === 'backdropClick') return;
        onClose();
      }}
      disableEscapeKeyDown={activeStep < 3}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: theme.palette.background.paper,
          overflow: 'hidden'
        }
      }}
    >
      {/* Header with progress */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          p: 3,
          position: 'relative'
        }}
      >
        {activeStep >= 3 && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'white'
            }}
          >
            <Close />
          </IconButton>
        )}

        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Welcome to Booker! 🚀
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.9, mb: 2 }}>
          Let's get you set up in under 5 minutes
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mt: 2 }}>
          {STEPS.map((label, index) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  '& .MuiStepLabel-label': {
                    color: 'white !important',
                    opacity: activeStep === index ? 1 : 0.6,
                    fontSize: '0.75rem'
                  },
                  '& .MuiStepIcon-root': {
                    color: alpha(theme.palette.common.white, 0.3),
                    '&.Mui-active': {
                      color: theme.palette.common.white
                    },
                    '&.Mui-completed': {
                      color: theme.palette.success.light
                    }
                  }
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Content */}
      <DialogContent sx={{ p: 4, minHeight: 400 }}>
        <Fade in key={activeStep}>
          <Box>{renderStepContent()}</Box>
        </Fade>
      </DialogContent>

      {/* Actions */}
      {activeStep < STEPS.length - 1 && (
        <Box
          sx={{
            p: 3,
            borderTop: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Button
            onClick={onClose}
            sx={{ textTransform: 'none' }}
          >
            Skip Setup
          </Button>

          <Box display="flex" gap={2}>
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                startIcon={<ArrowBack />}
                sx={{ textTransform: 'none' }}
              >
                Back
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
              disabled={loading}
              sx={{
                textTransform: 'none',
                px: 4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
              }}
            >
              {activeStep === STEPS.length - 2 ? 'Complete Setup' : 'Next'}
            </Button>
          </Box>
        </Box>
      )}

      {/* AI Suggestion Dialog */}
      <AISuggestionDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        title={aiDialogTitle}
        suggestions={aiSuggestions}
        loading={aiLoading}
        error={aiError}
        onAccept={handleAcceptSuggestion}
        onRegenerate={['businessName', 'services'].includes(aiType) ? handleRegenerateSuggestion : null}
        type={aiType}
      />

      {/* Quick Launch Flow */}
      <QuickLaunchFlow
        open={quickLaunchOpen}
        onClose={() => setQuickLaunchOpen(false)}
        onComplete={(action) => {
          setQuickLaunchOpen(false);
          if (action === 'customize') {
            // Stay in wizard — jump to Business Profile step so user can customize
            setActiveStep(1);
          } else {
            onComplete?.();
            navigate('/advanced-features?feature=group');
          }
        }}
      />
    </Dialog>
  );
};

// Step 1: Welcome
const WelcomeStep = ({ data, setData, theme, onQuickLaunch }) => {
  const selectedType = BUSINESS_TYPES.find(bt => bt.value === data.businessType);

  return (
    <Box textAlign="center" py={4}>
      <Avatar
        sx={{
          width: 100,
          height: 100,
          mx: 'auto',
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
        }}
      >
        <RocketLaunch sx={{ fontSize: 50 }} />
      </Avatar>

      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Let's Build Your Booking System
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 500, mx: 'auto' }}>
        We'll personalize your experience based on your business needs.
        This should take less than 5 minutes!
      </Typography>

      <FormControl component="fieldset" sx={{ mt: 4, width: '100%', maxWidth: 600, mx: 'auto' }}>
        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
          What best describes your business?
        </FormLabel>
        <RadioGroup
          value={data.businessType}
          onChange={(e) => setData({ ...data, businessType: e.target.value })}
        >
          {BUSINESS_TYPES.map(option => (
            <Paper
              key={option.value}
              elevation={data.businessType === option.value ? 3 : 0}
              sx={{
                p: 2,
                mb: 2,
                cursor: 'pointer',
                border: `2px solid ${data.businessType === option.value ? theme.palette.primary.main : theme.palette.divider}`,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  transform: 'translateY(-2px)'
                }
              }}
              onClick={() => setData({ ...data, businessType: option.value })}
            >
              <Box display="flex" alignItems="center">
                <Radio value={option.value} />
                <Box flex={1} textAlign="left">
                  <Typography fontWeight="bold">{option.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </RadioGroup>
      </FormControl>

      {/* Show tip based on selection */}
      {selectedType && (
        <Box
          mt={3}
          p={2}
          bgcolor={alpha(theme.palette.success.main, 0.1)}
          borderRadius={2}
          sx={{ maxWidth: 600, mx: 'auto' }}
        >
          <Typography variant="body2" color="success.main">
            ✨ {selectedType.features.showTip}
          </Typography>
        </Box>
      )}

      {/* Quick Launch CTA */}
      <Box
        mt={4}
        p={2.5}
        borderRadius={2}
        sx={{
          maxWidth: 600,
          mx: 'auto',
          border: `1px dashed ${alpha(theme.palette.primary.main, 0.4)}`,
          bgcolor: alpha(theme.palette.primary.main, 0.04)
        }}
      >
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Hosting one event right now?
        </Typography>
        <Button
          variant="outlined"
          onClick={onQuickLaunch}
          startIcon={<RocketLaunch />}
          sx={{ borderRadius: 3, fontWeight: 600 }}
        >
          Host One Event Now — 30 seconds
        </Button>
      </Box>
    </Box>
  );
};

// Step 2: Business Profile
const BusinessProfileStep = ({ data, setData, theme, validationErrors }) => {
  const handlePhoneChange = (value) => {
    setData({ ...data, businessPhone: value });
  };

  const handlePhoneBlur = () => {
    const validation = validatePhoneNumber(data.businessPhone);
    if (validation.isValid && validation.formatted) {
      setData({ ...data, businessPhone: validation.formatted });
    }
  };

  const handleAddressFieldChange = (field, value) => {
    setData({
      ...data,
      businessAddress: {
        ...data.businessAddress,
        [field]: sanitizeText(value)
      }
    });
  };

  return (
    <Box py={2}>
      <Box display="flex" alignItems="center" mb={3}>
        <Avatar
          sx={{
            width: 60,
            height: 60,
            mr: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
          }}
        >
          <Business sx={{ fontSize: 30 }} />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Business Profile
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This info will be used in your website and booking pages
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Business Name */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Business Name"
            placeholder="e.g., Jane's Hair Salon"
            value={data.businessName}
            onChange={(e) => {
              const name = sanitizeText(e.target.value);
              // Auto-generate slug from business name if slug is empty
              const slug = data.businessSlug || name.toLowerCase()
                .replace(/[^a-z0-9-]/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');

              setData({ ...data, businessName: name, businessSlug: slug });
            }}
            required
            error={!!validationErrors?.businessName}
            helperText={validationErrors?.businessName || 'Required (2-100 characters)'}
            variant="outlined"
          />
        </Grid>

        {/* Business Slug (Website URL) */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Website URL"
            placeholder="your-business-name"
            value={data.businessSlug}
            onChange={(e) => {
              const slug = e.target.value.toLowerCase()
                .replace(/[^a-z0-9-]/g, '-')
                .replace(/-+/g, '-')
                .replace(/^-|-$/g, '');
              setData({ ...data, businessSlug: slug });
            }}
            helperText={data.businessSlug
              ? `Your website: ${window.location.origin}/book/${data.businessSlug}`
              : 'URL-friendly name (automatically generated from business name)'
            }
            variant="outlined"
          />
        </Grid>

        {/* Owner/Founder Name */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Owner / Founder Name"
            placeholder="e.g., Jane Doe"
            value={data.ownerName}
            onChange={(e) => setData({ ...data, ownerName: sanitizeText(e.target.value) })}
            required
            helperText="Your name - appears on About Us page"
            variant="outlined"
          />
        </Grid>

        {/* Founded Year */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Year Founded"
            value={data.foundedYear}
            onChange={(e) => setData({ ...data, foundedYear: parseInt(e.target.value) })}
            helperText="Optional - shows experience (e.g., 'Since 2020')"
            variant="outlined"
          >
            {Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i).map(year => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Tagline */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Tagline / Slogan"
            placeholder="e.g., Quality service since 2020"
            value={data.tagline}
            onChange={(e) => setData({ ...data, tagline: sanitizeText(e.target.value) })}
            helperText="Optional - A catchy phrase for your business"
            variant="outlined"
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Business Description"
            placeholder="Tell your clients about your business..."
            value={data.businessDescription}
            onChange={(e) => setData({ ...data, businessDescription: sanitizeText(e.target.value) })}
            error={!!validationErrors?.businessDescription}
            helperText={validationErrors?.businessDescription || 'Optional - Appears on your website'}
            variant="outlined"
          />
        </Grid>

        {/* Service Area */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Service Area / City"
            placeholder="e.g., Dallas, TX"
            value={data.serviceArea}
            onChange={(e) => setData({ ...data, serviceArea: sanitizeText(e.target.value) })}
            helperText="Helps personalize your website copy"
            variant="outlined"
          />
        </Grid>

        {/* Brand Vibe */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Brand Vibe"
            value={data.brandVibe}
            onChange={(e) => setData({ ...data, brandVibe: e.target.value })}
            helperText="Sets the tone for your first website draft"
            variant="outlined"
          >
            {BRAND_VIBE_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Signature Services */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Signature Services (Optional)
          </Typography>
        </Grid>
        {data.signatureServices.map((service, index) => (
          <Grid item xs={12} md={4} key={`signature-service-${index}`}>
            <TextField
              fullWidth
              label={`Signature Service ${index + 1}`}
              placeholder="e.g., Skin Fade"
              value={service}
              onChange={(e) => {
                const updated = [...data.signatureServices];
                updated[index] = sanitizeText(e.target.value);
                setData({ ...data, signatureServices: updated });
              }}
              helperText={index === 0 ? 'Used for your website service highlights' : 'Optional'}
              variant="outlined"
            />
          </Grid>
        ))}

        {/* Phone */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Business Phone"
            placeholder="+1 (555) 123-4567"
            value={data.businessPhone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            onBlur={handlePhoneBlur}
            error={!!validationErrors?.businessPhone}
            helperText={validationErrors?.businessPhone || 'Optional - For client contact'}
            variant="outlined"
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Business Email"
            type="email"
            placeholder="contact@yourbusiness.com"
            value={data.businessEmail}
            onChange={(e) => setData({ ...data, businessEmail: sanitizeText(e.target.value) })}
            error={!!validationErrors?.businessEmail}
            helperText={validationErrors?.businessEmail || 'Optional - Contact email'}
            variant="outlined"
          />
        </Grid>

        {/* Address */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
            Business Address (Optional)
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Street Address"
            placeholder="123 Main Street"
            value={data.businessAddress.street}
            onChange={(e) => handleAddressFieldChange('street', e.target.value)}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="City"
            placeholder="San Francisco"
            value={data.businessAddress.city}
            onChange={(e) => handleAddressFieldChange('city', e.target.value)}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="State / Province"
            placeholder="CA"
            value={data.businessAddress.state}
            onChange={(e) => handleAddressFieldChange('state', e.target.value)}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Postal Code"
            placeholder="94105"
            value={data.businessAddress.postalCode}
            onChange={(e) => handleAddressFieldChange('postalCode', e.target.value)}
            variant="outlined"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Country"
            placeholder="United States"
            value={data.businessAddress.country}
            onChange={(e) => handleAddressFieldChange('country', e.target.value)}
            variant="outlined"
          />
        </Grid>

        {/* Industry */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Industry"
            value={data.industry}
            onChange={(e) => setData({ ...data, industry: e.target.value })}
            required
            variant="outlined"
            helperText="We'll customize your theme based on your industry"
          >
            {INDUSTRY_OPTIONS.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        {/* Timezone */}
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Timezone"
            value={data.timezone}
            onChange={(e) => setData({ ...data, timezone: e.target.value })}
            variant="outlined"
          >
            {TIMEZONES.map(tz => (
              <MenuItem key={tz.value} value={tz.value}>
                {tz.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      {data.industry && (
        <Box mt={3} p={2} bgcolor={alpha(theme.palette.success.main, 0.1)} borderRadius={2}>
          <Typography variant="body2" color="success.main">
            ✨ Great! We'll apply a {INDUSTRY_OPTIONS.find(opt => opt.value === data.industry)?.label} theme to your booking system and pre-fill your website with this information!
          </Typography>
        </Box>
      )}
    </Box>
  );
};

// Step 3: First Service
const ServiceStep = ({ data, setData, theme }) => (
  <Box py={2}>
    <Box display="flex" alignItems="center" mb={3}>
      <Avatar
        sx={{
          width: 60,
          height: 60,
          mr: 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
        }}
      >
        <Schedule sx={{ fontSize: 30 }} />
      </Avatar>
      <Box>
        <Typography variant="h5" fontWeight="bold">
          Create Your First Service
        </Typography>
        <Typography variant="body2" color="text.secondary">
          What service do you offer? (You can add more later)
        </Typography>
      </Box>
    </Box>

    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Service Name"
          placeholder="e.g., Haircut, Consultation, Training Session"
          value={data.serviceName}
          onChange={(e) => setData({ ...data, serviceName: e.target.value })}
          required
          variant="outlined"
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Duration (minutes)"
          value={data.serviceDuration}
          onChange={(e) => setData({ ...data, serviceDuration: parseInt(e.target.value) || 0 })}
          required
          variant="outlined"
          inputProps={{ min: 5, max: 480 }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="number"
          label="Price ($)"
          value={data.servicePrice}
          onChange={(e) => setData({ ...data, servicePrice: parseFloat(e.target.value) || 0 })}
          variant="outlined"
          inputProps={{ min: 0, step: 0.01 }}
          helperText="Leave as 0 for free services"
        />
      </Grid>
    </Grid>

    {data.industry && (
      <Box mt={3}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Templates for {INDUSTRY_OPTIONS.find(opt => opt.value === data.industry)?.label}:
        </Typography>
        <Box display="flex" flexWrap="wrap" gap={1}>
          {SERVICE_TEMPLATES[data.industry]?.map((template, idx) => (
            <Chip
              key={idx}
              label={`${template.name} (${template.duration}min, $${template.price})`}
              onClick={() => setData({
                ...data,
                serviceName: template.name,
                serviceDuration: template.duration,
                servicePrice: template.price
              })}
              color="primary"
              variant={
                data.serviceName === template.name &&
                data.serviceDuration === template.duration &&
                data.servicePrice === template.price
                  ? 'filled'
                  : 'outlined'
              }
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Box>
      </Box>
    )}
  </Box>
);

// Step 4: Availability
const AvailabilityStep = ({ data, setData, theme }) => {
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  return (
    <Box py={2}>
      <Box display="flex" alignItems="center" mb={3}>
        <Avatar
          sx={{
            width: 60,
            height: 60,
            mr: 2,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
          }}
        >
          <CalendarMonth sx={{ fontSize: 30 }} />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            Set Your Availability
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When are you available for bookings? (You can customize this later)
          </Typography>
        </Box>
      </Box>

      <Box>
        {days.map((day, idx) => {
          const dayKey = dayKeys[idx];
          const dayData = data.availability[dayKey];

          return (
            <Paper
              key={day}
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                border: `1px solid ${theme.palette.divider}`,
                opacity: dayData.enabled ? 1 : 0.5
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={dayData.enabled}
                        onChange={(e) => setData({
                          ...data,
                          availability: {
                            ...data.availability,
                            [dayKey]: { ...dayData, enabled: e.target.checked }
                          }
                        })}
                      />
                    }
                    label={day}
                  />
                </Grid>

                {dayData.enabled && (
                  <>
                    <Grid item xs={6} sm={4}>
                      <TextField
                        fullWidth
                        type="time"
                        label="Start"
                        value={dayData.start}
                        onChange={(e) => setData({
                          ...data,
                          availability: {
                            ...data.availability,
                            [dayKey]: { ...dayData, start: e.target.value }
                          }
                        })}
                        size="small"
                      />
                    </Grid>

                    <Grid item xs={6} sm={4}>
                      <TextField
                        fullWidth
                        type="time"
                        label="End"
                        value={dayData.end}
                        onChange={(e) => setData({
                          ...data,
                          availability: {
                            ...data.availability,
                            [dayKey]: { ...dayData, end: e.target.value }
                          }
                        })}
                        size="small"
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

// Step 5: Success
const SuccessStep = ({ data, theme, onComplete }) => {
  const slug = data.businessSlug || (data.businessName || '').toLowerCase().replace(/[^a-z0-9-]/g, '-');
  const bookingUrl = slug ? `${window.location.origin}/book/${slug}` : '';

  const handleCopyLink = async () => {
    if (!bookingUrl) return;
    try {
      await navigator.clipboard.writeText(bookingUrl);
      toast.success('Booking link copied!');
    } catch {
      toast.error('Could not copy. Select and copy manually.');
    }
  };

  const handleOpenLink = () => {
    if (!bookingUrl) return;
    window.open(bookingUrl, '_blank', 'noopener,noreferrer');
  };

  const handleEmailShare = () => {
    if (!bookingUrl) return;
    const subject = encodeURIComponent(`Book with ${data.businessName || 'us'}`);
    const body = encodeURIComponent(
      `Hi,\n\nYou can book an appointment here:\n${bookingUrl}\n\nThanks!`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleBuildWebsite = async () => {
    sessionStorage.setItem('websiteStartData', JSON.stringify({
      businessName: data.businessName,
      industry: data.industry,
      description: data.businessDescription,
      slug: data.businessSlug,
      address: data.businessAddress,
      phone: data.businessPhone,
      email: data.businessEmail,
      ownerName: data.ownerName,
      foundedYear: data.foundedYear,
      tagline: data.tagline
    }));
    if (onComplete) await onComplete();
    window.location.href = '/website-builder';
  };

  const handleGoToDashboard = async () => {
    if (onComplete) await onComplete();
  };

  return (
    <Box textAlign="center" py={4}>
      <Avatar
        sx={{
          width: 120,
          height: 120,
          mx: 'auto',
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.light} 100%)`
        }}
      >
        <Celebration sx={{ fontSize: 60 }} />
      </Avatar>

      <Typography variant="h4" fontWeight="bold" gutterBottom>
        🎉 You're All Set!
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph sx={{ maxWidth: 500, mx: 'auto' }}>
        Your booking system is ready to go! Here's what we set up for you:
      </Typography>

      <Grid container spacing={2} sx={{ maxWidth: 600, mx: 'auto', mt: 3 }}>
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <CheckCircle color="success" />
              <Box textAlign="left">
                <Typography variant="subtitle2" fontWeight="bold">
                  Business Profile
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.businessName} • {INDUSTRY_OPTIONS.find(opt => opt.value === data.industry)?.label}
                  {data.serviceArea ? ` • ${data.serviceArea}` : ''}
                  {data.brandVibe ? ` • ${BRAND_VIBE_OPTIONS.find(opt => opt.value === data.brandVibe)?.label || data.brandVibe}` : ''}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <CheckCircle color="success" />
              <Box textAlign="left">
                <Typography variant="subtitle2" fontWeight="bold">
                  First Service Created
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.serviceName} • {data.serviceDuration}min • ${data.servicePrice}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <CheckCircle color="success" />
              <Box textAlign="left">
                <Typography variant="subtitle2" fontWeight="bold">
                  Availability Configured
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Object.values(data.availability).filter(d => d.enabled).length} days per week
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Share-link hero — primary activation surface */}
      {bookingUrl && (
        <Paper
          elevation={4}
          sx={{
            mt: 5,
            p: { xs: 3, sm: 4 },
            maxWidth: 640,
            mx: 'auto',
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
            border: `2px solid ${theme.palette.primary.main}`,
          }}
        >
          <Typography variant="overline" color="primary" fontWeight="bold">
            Your booking link is live
          </Typography>
          <Typography variant="h6" fontWeight="bold" sx={{ mt: 0.5, mb: 2 }}>
            Share this to get your first booking
          </Typography>

          <TextField
            fullWidth
            value={bookingUrl}
            InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.9rem' } }}
            onFocus={(e) => e.target.select()}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<ContentCopy />}
              onClick={handleCopyLink}
              sx={{ px: 3, fontWeight: 700 }}
            >
              Copy link
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<OpenInNew />}
              onClick={handleOpenLink}
              sx={{ px: 3 }}
            >
              Open
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<EmailIcon />}
              onClick={handleEmailShare}
              sx={{ px: 3 }}
            >
              Email it
            </Button>
          </Box>
        </Paper>
      )}

      {/* Primary CTA — go to dashboard */}
      <Box sx={{ mt: 4 }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Dashboard />}
          onClick={handleGoToDashboard}
          sx={{
            px: 6,
            py: 1.75,
            fontSize: '1rem',
            fontWeight: 700,
          }}
        >
          Go to dashboard
        </Button>
      </Box>

      {/* Secondary actions — demoted to text links */}
      <Box sx={{ mt: 3, display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="text"
          size="small"
          startIcon={<Web fontSize="small" />}
          onClick={handleBuildWebsite}
          sx={{ color: 'text.secondary' }}
        >
          Build a website
        </Button>
        <Button
          variant="text"
          size="small"
          startIcon={<Phone fontSize="small" />}
          href="https://connect.futuresenseai.com?from=booker"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'text.secondary' }}
        >
          Set up AI call answering
        </Button>
      </Box>
    </Box>
  );
};

export default OnboardingWizard;
